import { apiFetch } from '@/lib/apiClient'
import type { ServiceType } from '@/features/cases'
import type {
  BuildGoalInput,
  OnboardingDocumentKind,
} from '@/features/onboarding/store/onboarding-store'

export interface SubmitPublicCaseInput {
  serviceType: ServiceType
  mobile: string
  consultationSlotAt: string
  document?: { file: File; kind: OnboardingDocumentKind; password?: string }
}

/** Mirrors CreatePublicCaseResultDto exactly — POST /public/cases returns nothing else. */
export interface SubmitPublicCaseResult {
  caseReference: string
  trackingToken: string
}

export interface SubmitPublicBuildPortfolioCaseInput {
  mobile: string
  consultationSlotAt: string
  clientDetails: { name: string; age: number; city?: string; email?: string }
  expenses: {
    monthlyIncome: string
    rentEmi: string
    utilitiesGroceries: string
    transportLifestyle: string
    otherExpenses: string
  }
  // `BuildGoalInput` (the onboarding store's own type), not the schema's discriminated-union
  // `GoalFormValues` — the store only ever holds goals `goalSchema` already validated
  // (`buildPortfolioGoalsView.tsx`'s `parsedGoals`), but Zustand state can't carry a discriminated
  // union's per-branch typing through `set()`, so this takes the same loose-but-already-validated
  // shape the store exposes.
  goals: BuildGoalInput[]
}

/** Mirrors CreatePublicCaseResultDto — same result shape as `SubmitPublicCaseResult` above, kept
 * as its own type since the two requests are otherwise unrelated. */
export interface SubmitPublicBuildPortfolioCaseResult {
  caseReference: string
  trackingToken: string
}

/**
 * `POST /public/cases/build-portfolio` — `@Public()`, plain JSON, no multipart: unlike
 * `submitPublicCase` above, Build's intake has no document to upload, so this calls `apiFetch`
 * directly instead of `useFileUpload`'s multipart path (mirrors
 * `features/public/api/public.api.ts`'s unauthenticated-JSON-`apiFetch` convention). Lives here,
 * next to `submitPublicCase`, rather than in `features/build-portfolio/`, because the shared final
 * step (`submission-successful-view.tsx`) that calls it lives in this feature — keeping it here
 * avoids a circular import between the two feature barrels (`build-portfolio` already depends on
 * `onboarding` for the store/layout; the dependency can't run the other way too).
 */
export function submitPublicBuildPortfolioCase(
  input: SubmitPublicBuildPortfolioCaseInput,
): Promise<SubmitPublicBuildPortfolioCaseResult> {
  return apiFetch<SubmitPublicBuildPortfolioCaseResult>('/api/v1/public/cases/build-portfolio', {
    method: 'POST',
    body: JSON.stringify(input),
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  })
}
